import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Card, CardContent } from '../../../components/tailwind/card';
import { Button } from '../../../components/tailwind/button';
import { ExternalLink, Phone, MessageSquare, Clipboard } from 'lucide-react';
import { X } from 'lucide-react';

export default function ButtonsSection({ formData = {}, setFormData, validationErrors = {} }) {
  const [open, setOpen] = useState(false);
  const buttons = Array.isArray(formData.buttons) ? formData.buttons : [];
  const wrapperRef = useRef(null);
  const [placement, setPlacement] = useState('bottom');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    const wrap = wrapperRef.current;
    if (!wrap) return;
    const wrapRect = wrap.getBoundingClientRect();
    const approxHeight = 160;
    const spaceBelow = window.innerHeight - wrapRect.bottom;
    if (spaceBelow < approxHeight && wrapRect.top > approxHeight) {
      setPlacement('top');
    } else {
      setPlacement('bottom');
    }
  }, [open]);

  const addButton = (type) => {
    setErrorMessage('');
    const quickCount = buttons.filter((b) => b.type === 'QUICK_REPLY').length;
    const ctaCount = buttons.filter((b) => ['URL', 'CALL', 'COPY'].includes(b.type)).length;
    const copyCount = buttons.filter((b) => b.type === 'COPY').length;

    if (buttons.length >= 10) {
      setErrorMessage('You can add up to 10 buttons only.');
      return;
    }
    if (type === 'QUICK_REPLY' && ctaCount > 0) {
      setErrorMessage('Quick Reply buttons cannot be mixed with CTA buttons.');
      return;
    }
    if (['URL', 'CALL', 'COPY'].includes(type) && quickCount > 0) {
      setErrorMessage('Cannot add CTA buttons when Quick Reply buttons exist.');
      return;
    }
    if (type === 'COPY' && copyCount > 0) {
      setErrorMessage('Only one Copy Code button is allowed per template.');
      return;
    }
    const id = Date.now();
    const base = {
      id,
      type,
      text: type === 'QUICK_REPLY' ? 'Quick reply' : type === 'URL' ? 'Visit website' : type === 'CALL' ? 'Call phone number' : 'Copy code',
      urlType: 'STATIC',
      url: '',
      urlExample: '',
      country: 'IN',
      phoneNumber: '',
      sample: '',
    };
    setFormData('buttons', [...buttons, base]);
    setOpen(false);
  };

  const updateButton = (id, patch) => {
    const next = buttons.map((b) => (b.id === id ? { ...b, ...patch } : b));
    setFormData('buttons', next);
  };

  const removeButton = (id) => {
    const next = buttons.filter((b) => b.id !== id);
    setFormData('buttons', next);
  };

  return (
    <Card className="border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">Buttons · Optional</h3>
            <p className="text-xs text-gray-500">Create buttons that let customers respond or take action.</p>
          </div>

            <div className="relative" ref={wrapperRef}>
            <Button
              className="cursor-pointer bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800"
              variant="outline"
              size="sm"
              onClick={() => setOpen((v) => !v)}
            >
              + Add button
            </Button>

            {open && (
              <div className={`${placement === 'bottom' ? 'absolute right-0 mt-2' : 'absolute right-0 bottom-full mb-2'} w-48 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded shadow z-20`}> 
                  <div className="flex flex-col">
                    <button
                      onClick={() => addButton('QUICK_REPLY')}
                      disabled={buttons.length >= 10 || buttons.some((b) => ['URL','CALL','COPY'].includes(b.type))}
                      className={`flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer text-gray-700 dark:text-gray-200 ${buttons.length >= 10 || buttons.some((b) => ['URL','CALL','COPY'].includes(b.type)) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <MessageSquare className="w-4 h-4" />
                      <span className="text-sm">Quick Reply</span>
                    </button>
                    <button
                      onClick={() => addButton('URL')}
                      disabled={buttons.length >= 10 || buttons.some((b) => b.type === 'QUICK_REPLY')}
                      className={`flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer text-gray-700 dark:text-gray-200 ${buttons.length >= 10 || buttons.some((b) => b.type === 'QUICK_REPLY') ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <ExternalLink className="w-4 h-4" />
                      <span className="text-sm">Website Link</span>
                    </button>
                    <button
                      onClick={() => addButton('CALL')}
                      disabled={buttons.length >= 10 || buttons.some((b) => b.type === 'QUICK_REPLY')}
                      className={`flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer text-gray-700 dark:text-gray-200 ${buttons.length >= 10 || buttons.some((b) => b.type === 'QUICK_REPLY') ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Phone className="w-4 h-4" />
                      <span className="text-sm">Call Phone Number</span>
                    </button>
                    <button
                      onClick={() => addButton('COPY')}
                      disabled={buttons.length >= 10 || buttons.some((b) => b.type === 'QUICK_REPLY') || buttons.some((b) => b.type === 'COPY')}
                      className={`flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer text-gray-700 dark:text-gray-200 ${buttons.length >= 10 || buttons.some((b) => b.type === 'QUICK_REPLY') || buttons.some((b) => b.type === 'COPY') ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Clipboard className="w-4 h-4" />
                      <span className="text-sm">Copy Code</span>
                    </button>
                  </div>
                </div>
            )}
          </div>
        </div>

        <div className="space-y-3">
          {buttons.length === 0 && <div className="text-sm text-gray-500 dark:text-gray-400">No buttons added.</div>}
          {errorMessage && <p className="text-sm text-red-500">{errorMessage}</p>}
          {validationErrors && validationErrors.buttons && (
            <p className="text-sm text-red-500">{validationErrors.buttons}</p>
          )}

          {buttons.map((btn, idx) => (
            <div key={btn.id} className="border rounded p-3 bg-gray-50 dark:bg-slate-800 border-gray-200 dark:border-slate-700">
              <div className="flex items-start justify-between">
                <div className="font-medium text-gray-900 dark:text-white">{idx + 1}. {btn.type === 'QUICK_REPLY' ? 'Quick Reply' : btn.type === 'URL' ? 'Website Link' : btn.type === 'CALL' ? 'Call' : 'Copy Code'}</div>
                <button
                  onClick={() => removeButton(btn.id)}
                  className="text-red-500 hover:text-red-600 p-1 rounded focus:outline-none focus:ring-2 focus:ring-red-300"
                  aria-label={`Remove button ${idx + 1}`}
                  title="Remove"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-3">
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-300">Button text</label>
                  <input
                    value={btn.text}
                    onChange={(e) => updateButton(btn.id, { text: e.target.value })}
                    placeholder="Button text"
                    maxLength={25}
                    className="mt-1 w-full border rounded px-2 py-1 bg-white dark:bg-slate-950 text-gray-900 dark:text-white border-gray-200 dark:border-slate-800"
                  />
                </div>

                {btn.type === 'URL' && (
                  <>
                    <div className="flex gap-3">
                      <div>
                        <label className="block text-sm text-gray-600 dark:text-gray-300">URL type</label>
                        <select
                          value={btn.urlType}
                          onChange={(e) => updateButton(btn.id, { urlType: e.target.value })}
                          className="mt-1 border rounded px-2 py-1 bg-white dark:bg-slate-950 text-gray-900 dark:text-white border-gray-200 dark:border-slate-800"
                        >
                          <option value="STATIC">Static</option>
                          <option value="DYNAMIC">Dynamic</option>
                        </select>
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm text-gray-600 dark:text-gray-300">Website URL</label>
                        <input
                          value={btn.url}
                          onChange={(e) => updateButton(btn.id, { url: e.target.value })}
                          placeholder={btn.urlType === 'DYNAMIC' ? "https://www.example.com/{{variable_name}}" : "https://www.example.com"}
                          className="mt-1 w-full border rounded px-2 py-1 bg-white dark:bg-slate-950 text-gray-900 dark:text-white border-gray-200 dark:border-slate-800"
                        />
                      </div>
                    </div>
                    {btn.urlType === 'DYNAMIC' && (
                      <div>
                        <label className="block text-sm text-gray-600 dark:text-gray-300">URL Variable Example</label>
                        <input
                          value={btn.urlExample}
                          onChange={(e) => updateButton(btn.id, { urlExample: e.target.value })}
                          placeholder="ORD-20240201-XYZ"
                          maxLength={200}
                          className="mt-1 w-full border rounded px-2 py-1 bg-white dark:bg-slate-950 text-gray-900 dark:text-white border-gray-200 dark:border-slate-800"
                        />
                      </div>
                    )}
                  </>
                )}

                {btn.type === 'CALL' && (
                  <div className="flex flex-col">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Country code +91 will be applied automatically</p>
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-300">Phone number</label>
                      <input
                        value={btn.phoneNumber}
                        onChange={(e) => updateButton(btn.id, { phoneNumber: e.target.value })}
                        placeholder="Enter phone number"
                        maxLength={20}
                        className="mt-1 w-full border rounded px-2 py-1 bg-white dark:bg-slate-950 text-gray-900 dark:text-white border-gray-200 dark:border-slate-800"
                      />
                    </div>
                  </div>
                )}

                {btn.type === 'COPY' && (
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-300">Offer code sample</label>
                      <input
                        value={btn.sample}
                        onChange={(e) => updateButton(btn.id, { sample: e.target.value })}
                        placeholder="Enter sample text"
                        maxLength={20}
                        className="mt-1 w-full border rounded px-2 py-1 bg-white dark:bg-slate-950 text-gray-900 dark:text-white border-gray-200 dark:border-slate-800"
                      />
                  </div>
                )}

              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
